import { useEffect, useState, useCallback } from "react";
import { logError } from "@/lib/logError";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, CheckCheck, Settings, Send, Loader2 } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { frCA } from "date-fns/locale";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { canUsePWA } from "@/lib/pwa";
import { isPushSupported, subscribePush, unsubscribePush, getPushSubscription } from "@/lib/push";

const TYPE_LABEL = {
  report_signed: "Rapport de pathologie",
  ai_complete: "Analyse IA",
  urgent_case: "Cas urgent",
  garde_reminder: "Garde",
  mention: "Mention",
  new_patient: "Patient",
  system: "Système",
};

export default function Notifications() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const pwa = canUsePWA(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/notifications/history", { params: { limit: 100 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isPushSupported()) getPushSubscription().then((s) => setPushOn(!!s)).catch(logError("État des notifications push"));
  }, []);

  const togglePush = async () => {
    setBusy(true);
    try {
      if (pushOn) { await unsubscribePush(); setPushOn(false); toast.success("Notifications push désactivées"); }
      else { await subscribePush(); setPushOn(true); toast.success("Notifications push activées"); }
    } catch (e) {
      toast.error(e?.message || "Action impossible");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    try { await api.post("/push/test"); toast.success("Notification test envoyée"); setTimeout(load, 800); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch (e) { logError("Tout marquer comme lu")(e); }
  };

  const openItem = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch (e) { logError("Marquer comme lu")(e); } }
    navigate(n.url || "/");
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader title={t("nav_notifications")} subtitle="Historique et préférences de notification">
        <Button variant="outline" size="sm" onClick={() => navigate("/profile")} data-testid="notif-prefs-btn">
          <Settings className="h-4 w-4 mr-1.5" /> Préférences
        </Button>
        <Button variant="outline" size="sm" onClick={markAll} data-testid="notif-markall-btn">
          <CheckCheck className="h-4 w-4 mr-1.5" /> Tout marquer lu
        </Button>
      </PageHeader>

      {pwa && (
        <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3"
             data-testid="push-control-card">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${pushOn ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-400"}`}>
            {pushOn ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-800">
              Notifications push {pushOn ? "activées" : "désactivées"} sur cet appareil
            </div>
            <p className="text-xs text-slate-500">
              {isPushSupported()
                ? "Recevez les alertes même lorsque l'application est fermée."
                : "Non supporté sur cet appareil/navigateur (iOS : installez d'abord la PWA)."}
            </p>
          </div>
          {pushOn && (
            <Button variant="ghost" size="sm" onClick={sendTest} data-testid="push-test-btn">
              <Send className="h-4 w-4 mr-1.5" /> Test
            </Button>
          )}
          <Button size="sm" disabled={busy || !isPushSupported()} onClick={togglePush} data-testid="push-toggle-btn">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {pushOn ? "Désactiver" : "Activer"}
          </Button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400" data-testid="notif-empty">
            Aucune notification pour le moment
          </div>
        ) : items.map((n) => (
          <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-row-${n.id}`}
                  className={`w-full text-left px-5 py-3.5 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-start gap-3
                              ${n.read ? "" : "bg-sky-50/50"}`}>
            {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-sky-500 flex-shrink-0" />}
            <div className={`flex-1 min-w-0 ${n.read ? "pl-5" : ""}`}>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 bg-sky-50 rounded px-1.5 py-0.5">
                  {TYPE_LABEL[n.type] || "Info"}
                </span>
                <span className="text-sm font-medium text-slate-800">{n.title}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>
              <div className="text-[10px] text-slate-400 mt-1">
                {n.sent_at ? formatDistanceToNow(parseISO(n.sent_at), { addSuffix: true, locale: frCA }) : ""}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
